import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bug, Clipboard, ImagePlus, Loader2, X } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { Label } from "@/shared/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/shared/components/ui/radio-group";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
} from "@/shared/components/ui/sheet";
import { createBugReport } from "../services/bugReportService";

interface BugReportDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

type ReportType = "suggestion" | "bug" | "other";

const MAX_FILES = 5;
const MAX_SIZE_MB = 10;

function ImagePreview({ file, onRemove }: { file: File; onRemove: () => void }) {
    const url = URL.createObjectURL(file);
    return (
        <div className="relative group w-20 h-20 shrink-0">
            <img
                src={url}
                alt={file.name}
                className="w-full h-full object-cover rounded-lg border border-border"
                onLoad={() => URL.revokeObjectURL(url)}
            />
            <button
                type="button"
                onClick={onRemove}
                className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-background border border-border flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
            >
                <X className="w-3 h-3" />
            </button>
            <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[9px] px-1 py-0.5 rounded-b-lg truncate">
                {(file.size / 1024).toFixed(0)} KB
            </div>
        </div>
    );
}

export function BugReportDialog({ open, onOpenChange }: BugReportDialogProps) {
    const { t } = useTranslation("bugReports");
    const fileInputRef = useRef<HTMLInputElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const [reportType, setReportType] = useState<ReportType>("bug");
    const [otherText, setOtherText] = useState("");
    const [description, setDescription] = useState("");
    const [driveLink, setDriveLink] = useState("");
    const [files, setFiles] = useState<File[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState<string | null>(null);

    function reset() {
        setReportType("bug");
        setOtherText("");
        setDescription("");
        setDriveLink("");
        setFiles([]);
        setSuccess(false);
        setError(null);
    }

    function handleClose(nextOpen: boolean) {
        if (!nextOpen) {
            if (success) {
                reset();
            } else {
                setError(null);
            }
        }
        onOpenChange(nextOpen);
    }

    function addFiles(incoming: File[]) {
        setFiles((prev) => {
            const merged = [...prev];
            for (const f of incoming) {
                if (merged.length >= MAX_FILES) break;
                if (f.size > MAX_SIZE_MB * 1024 * 1024) {
                    setError(t("fileTooLarge", { name: f.name, max: MAX_SIZE_MB }));
                    continue;
                }
                if (!merged.find((x) => x.name === f.name && x.size === f.size)) {
                    merged.push(f);
                }
            }
            return merged;
        });
        setError(null);
    }

    function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
        addFiles(Array.from(e.target.files ?? []));
        e.target.value = "";
    }

    function handleDescriptionPaste(e: React.ClipboardEvent<HTMLTextAreaElement>) {
        const items = Array.from(e.clipboardData.items).filter((i) =>
            i.type.startsWith("image/")
        );
        if (items.length === 0) return;

        const pasted: File[] = [];
        for (const item of items) {
            const file = item.getAsFile();
            if (!file) continue;
            const ext = item.type.split("/")[1] || "png";
            pasted.push(new File([file], `captura-${Date.now()}.${ext}`, { type: item.type }));
        }
        if (pasted.length > 0) addFiles(pasted);
    }

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        if (!description.trim()) return;
        if (reportType === "other" && !otherText.trim()) return;

        const title =
            reportType === "suggestion"
                ? t("types.suggestion")
                : reportType === "bug"
                  ? t("types.bug")
                  : otherText.trim();

        setSubmitting(true);
        setError(null);
        try {
            await createBugReport({
                title,
                description: description.trim(),
                report_type: reportType,
                other_type_text: reportType === "other" ? otherText.trim() : undefined,
                page_url: window.location.href,
                drive_link: driveLink.trim() || undefined,
                attachments: files,
            });
            setSuccess(true);
            setTimeout(() => {
                reset();
                onOpenChange(false);
            }, 2000);
        } catch {
            setError(t("submitError"));
        } finally {
            setSubmitting(false);
        }
    }

    const canSubmit =
        description.trim().length > 0 &&
        (reportType !== "other" || otherText.trim().length > 0);

    return (
        <Sheet open={open} onOpenChange={handleClose}>
            <SheetContent
                side="right"
                className="w-full sm:max-w-[520px] flex flex-col gap-0 p-0 bg-background"
            >
                <SheetHeader className="px-6 pt-5 pb-4 bg-background border-b">
                    <SheetTitle className="flex items-center gap-2 text-base">
                        <Bug className="w-4 h-4 text-destructive shrink-0" />
                        {t("title")}
                    </SheetTitle>
                </SheetHeader>

                {success ? (
                    <div className="flex-1 flex flex-col items-center justify-center gap-3 px-6 text-center">
                        <div className="w-14 h-14 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                            <svg
                                className="w-7 h-7 text-green-600 dark:text-green-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M5 13l4 4L19 7"
                                />
                            </svg>
                        </div>
                        <p className="text-sm font-semibold text-foreground">
                            {t("successTitle")}
                        </p>
                        <p className="text-xs text-muted-foreground max-w-xs">
                            {t("successDesc")}
                        </p>
                    </div>
                ) : (
                    <form
                        onSubmit={handleSubmit}
                        className="flex-1 flex flex-col overflow-hidden"
                    >
                        <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-3">
                            {/* Card 1 — Type */}
                            <div className="bg-background rounded-xl border border-border p-4 flex flex-col gap-3">
                                <p className="text-sm font-medium leading-snug">
                                    {t("typeQuestion")}{" "}
                                    <span className="text-destructive">*</span>
                                </p>
                                <RadioGroup
                                    value={reportType}
                                    onValueChange={(v) => setReportType(v as ReportType)}
                                    className="flex flex-col gap-2"
                                >
                                    {(["suggestion", "bug", "other"] as const).map((val) => (
                                        <div key={val} className="flex items-start gap-2.5">
                                            <RadioGroupItem
                                                value={val}
                                                id={`type-${val}`}
                                                className="mt-0.5 shrink-0"
                                            />
                                            <div className="flex-1 flex flex-col gap-1">
                                                <Label
                                                    htmlFor={`type-${val}`}
                                                    className="text-sm cursor-pointer leading-snug"
                                                >
                                                    {t(`types.${val}`)}
                                                </Label>
                                                {val === "other" && reportType === "other" && (
                                                    <input
                                                        type="text"
                                                        maxLength={200}
                                                        value={otherText}
                                                        onChange={(e) => setOtherText(e.target.value)}
                                                        placeholder={t("otherPlaceholder")}
                                                        className="h-8 px-2 border-b border-input bg-transparent text-sm placeholder:text-muted-foreground focus:outline-none focus:border-ring transition-colors"
                                                        autoFocus
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </RadioGroup>
                            </div>

                            {/* Card 2 — Description */}
                            <div className="bg-background rounded-xl border border-border p-4 flex flex-col gap-2">
                                <p className="text-sm font-medium leading-snug">
                                    {t("descriptionQuestion")}{" "}
                                    <span className="text-destructive">*</span>
                                </p>
                                <textarea
                                    ref={textareaRef}
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    onPaste={handleDescriptionPaste}
                                    placeholder={t("descriptionPlaceholder")}
                                    rows={5}
                                    className="w-full resize-none bg-transparent text-sm placeholder:text-muted-foreground focus:outline-none border-b border-input focus:border-ring transition-colors pb-1"
                                    required
                                />
                                {files.length === 0 && (
                                    <p className="text-[11px] text-muted-foreground flex items-center gap-1 mt-0.5">
                                        <Clipboard className="w-3 h-3" />
                                        {t("pasteHint")}
                                    </p>
                                )}
                            </div>

                            {/* Card 3 — Screenshots */}
                            <div className="bg-background rounded-xl border border-border p-4 flex flex-col gap-3">
                                <div className="flex items-center justify-between">
                                    <p className="text-sm font-medium">{t("screenshotsQuestion")}</p>
                                    <button
                                        type="button"
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={files.length >= MAX_FILES}
                                        className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                    >
                                        <ImagePlus className="w-3.5 h-3.5" />
                                        {t("addImages")} ({files.length}/{MAX_FILES})
                                    </button>
                                </div>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    className="hidden"
                                    onChange={handleFileChange}
                                />

                                {files.length > 0 ? (
                                    <div className="flex flex-wrap gap-2">
                                        {files.map((f, i) => (
                                            <ImagePreview
                                                key={i}
                                                file={f}
                                                onRemove={() =>
                                                    setFiles((prev) =>
                                                        prev.filter((_, idx) => idx !== i)
                                                    )
                                                }
                                            />
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-xs text-muted-foreground">
                                        {t("screenshotsEmpty")}
                                    </p>
                                )}
                            </div>

                            {/* Card 4 — Drive link */}
                            <div className="bg-background rounded-xl border border-border p-4 flex flex-col gap-2">
                                <p className="text-sm font-medium">{t("driveLinkQuestion")}</p>
                                <p className="text-xs text-muted-foreground leading-relaxed">
                                    {t("driveLinkDesc")}
                                </p>
                                <input
                                    type="url"
                                    value={driveLink}
                                    onChange={(e) => setDriveLink(e.target.value)}
                                    placeholder={t("driveLinkPlaceholder")}
                                    className="h-8 px-0 border-b border-input bg-transparent text-sm placeholder:text-muted-foreground focus:outline-none focus:border-ring transition-colors"
                                />
                            </div>

                            {error && (
                                <p className="text-xs text-destructive bg-destructive/10 px-3 py-2 rounded-lg">
                                    {error}
                                </p>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="px-5 py-4 bg-background border-t flex items-center justify-between gap-2">
                            <button
                                type="button"
                                onClick={() => reset()}
                                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                            >
                                {t("clearForm")}
                            </button>
                            <div className="flex items-center gap-2">
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleClose(false)}
                                    disabled={submitting}
                                >
                                    {t("cancel")}
                                </Button>
                                <Button
                                    type="submit"
                                    size="sm"
                                    disabled={submitting || !canSubmit}
                                >
                                    {submitting && (
                                        <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                                    )}
                                    {t("submit")}
                                </Button>
                            </div>
                        </div>
                    </form>
                )}
            </SheetContent>
        </Sheet>
    );
}
